/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';

interface EmptyProps {
  title?: string;
  description?: string;
  action?: ReactNode;
}

export function NotificationEmptyState({
  title = 'You’re all caught up',
  description = 'Run results, bid request updates and agent replies will show up here.',
  action,
}: Readonly<EmptyProps>) {
  return (
    <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center', px: 5, py: 6 }}>
      <Box
        sx={(theme) => ({
          width: 52,
          height: 52,
          mb: 0.5,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '16px',
          color: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.14),
        })}
      >
        <DoneAllRoundedIcon />
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', maxWidth: 280 }}>
        {description}
      </Typography>
      {action}
    </Stack>
  );
}

// Varied title widths read as real rows; also serve as stable keys.
const SKELETON_TITLE_WIDTHS = ['72%', '55%', '81%', '63%', '76%', '58%'];

export function NotificationSkeletonRows({ count = 5 }: Readonly<{ count?: number }>) {
  return (
    <Stack spacing={0.5} aria-busy aria-label="Loading notifications">
      {SKELETON_TITLE_WIDTHS.slice(0, count).map((titleWidth) => (
        <Stack key={titleWidth} direction="row" spacing={1.5} sx={{ p: '12px 12px 12px 10px' }}>
          <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '9px' }} />
          <Stack spacing={0.75} sx={{ flex: 1 }}>
            <Skeleton variant="rounded" width={titleWidth} height={11} />
            <Skeleton variant="rounded" height={9} />
            <Skeleton variant="rounded" width={90} height={8} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
